import { useEffect, useState } from "react";
import { X, Inbox, MessageSquare, Plus, Trash2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useSettings } from "@/context/SettingsContext";

const DEFAULT_TEMPLATES = [
  "Pracujemy nad usunięciem awarii. Przewidywany czas przywrócenia usługi: …",
  "Awaria została usunięta. W razie dalszych problemów prosimy o kontakt.",
  "Dziękujemy za zgłoszenie. Nasz technik skontaktuje się z Państwem wkrótce.",
  "Trwają prace planowe. Przepraszamy za chwilowe utrudnienia.",
  "Zgłoszenie {nr_zgłoszenia}: awaria w {miasto}, przewidywany czas usunięcia: {ETA}.",
];

export default function SmsInboxModal({ onClose, onChanged, canEdit = true, canDelete = true }) {
  const { t } = useI18n();
  const { settings } = useSettings();
  const templates = (settings?.sms_reply_templates && settings.sms_reply_templates.length) ? settings.sms_reply_templates : DEFAULT_TEMPLATES;
  const [inbox, setInbox] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/sms/inbox"); setInbox(data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try { const { data } = await api.get("/sms/inbox"); if (!cancelled) setInbox(data || []); }
      finally { if (!cancelled) setLoading(false); }
      await api.post("/sms/inbox/mark-read").catch(() => {});
      if (!cancelled && onChanged) onChanged();
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  const toTicket = async (m) => {
    try { const { data } = await api.post(`/sms/inbox/${m.id}/ticket`); toast.success(`${t("Utworzono zgłoszenie")} ${data.ticket_no}`); load(); onChanged && onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (m) => {
    if (!window.confirm(t("Usunąć tę wiadomość ze skrzynki?"))) return;
    try { await api.delete(`/sms/inbox/${m.id}`); toast.success(t("Usunięto wiadomość")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendReply = async (m) => {
    if (!replyText.trim()) return;
    setBusy(true);
    try { await api.post(`/sms/inbox/${m.id}/reply`, { text: replyText.trim() }); toast.success(t("Wysłano odpowiedź SMS")); setReplyFor(null); setReplyText(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[3000] bg-black/60 flex items-center justify-center p-4" data-testid="sms-inbox-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[82vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <div className="flex items-center gap-2 text-sm font-semibold text-white"><Inbox size={16} className="text-cyan-400" /> {t("Skrzynka SMS")} <span className="text-slate-500 font-normal">({inbox.length})</span></div>
          <button onClick={onClose} data-testid="sms-inbox-close" className="p-1 rounded hover:bg-slate-800"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto p-3 space-y-2">
          {loading ? <div className="text-xs text-slate-500 flex items-center gap-1.5"><Loader2 size={14} className="animate-spin" /> {t("Ładowanie…")}</div>
            : inbox.length === 0 ? <div className="text-xs text-slate-500" data-testid="sms-inbox-empty">{t("Brak przychodzących wiadomości SMS.")}</div>
              : inbox.map((m) => (
                <div key={m.id} data-testid="sms-inbox-item" className={`rounded-lg border p-2.5 ${m.read ? "border-slate-700 bg-slate-800/50" : "border-cyan-700/60 bg-cyan-950/20"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-slate-300 flex items-center gap-1.5"><MessageSquare size={13} className="text-cyan-400" /><b>{m.sender || "—"}</b>{m.client_name && <span className="text-slate-500">· {m.client_name}</span>}</div>
                    <span className="text-[10px] text-slate-500">{(m.ts || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-sm text-slate-200 mt-1 whitespace-pre-wrap">{m.text || "—"}</div>
                  {(m.replies || []).map((r, i) => (
                    <div key={i} className="text-xs text-emerald-200 bg-emerald-950/30 rounded px-2 py-1 mt-1 flex items-start gap-1.5"><Send size={11} className="mt-0.5 text-emerald-400 shrink-0" /><span>{r.text} <span className="text-slate-500">· {(r.ts || "").slice(0, 16).replace("T", " ")}</span></span></div>
                  ))}
                  <div className="flex items-center justify-between mt-2 gap-2">
                    <div className="flex flex-wrap gap-1">
                      {(m.attached_tickets || []).map((tk) => <span key={tk} className="text-[10px] rounded px-1.5 py-0.5 border border-cyan-700/50 text-cyan-300">{tk}</span>)}
                      {!(m.attached_tickets || []).length && <span className="text-[10px] text-slate-500">{t("Niepowiązany")}</span>}
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      {m.phone && canEdit && <button onClick={() => { setReplyFor(replyFor === m.id ? null : m.id); setReplyText(""); }} data-testid="sms-inbox-reply-toggle" className="text-[11px] rounded px-2 py-1 bg-emerald-900/40 hover:bg-emerald-900/60 text-emerald-300 flex items-center gap-1"><Send size={12} /> {t("Odpowiedz")}</button>}
                      {!m.created_ticket && !(m.attached_tickets || []).length && canEdit && <button onClick={() => toTicket(m)} data-testid="sms-inbox-create-ticket" className="text-[11px] rounded px-2 py-1 bg-red-900/40 hover:bg-red-900/60 text-red-300 flex items-center gap-1"><Plus size={12} /> {t("Utwórz zgłoszenie")}</button>}
                      {canDelete && <button onClick={() => del(m)} data-testid="sms-inbox-delete" title={t("Usuń")} className="text-[11px] rounded px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300"><Trash2 size={12} /></button>}
                    </div>
                  </div>
                  {replyFor === m.id && (
                    <div className="mt-2 space-y-1.5" data-testid="sms-inbox-reply-box">
                      <div className="flex flex-wrap gap-1" data-testid="sms-reply-templates">
                        {templates.map((tpl, ti) => (
                          <button key={ti} type="button" data-testid={`sms-reply-template-${ti}`} onClick={() => setReplyText(tpl)} title={tpl}
                            className="text-[10px] rounded-full px-2 py-1 border border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white">{tpl.length > 30 ? tpl.slice(0, 30) + "…" : tpl}</button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder={t("Treść odpowiedzi SMS…")} data-testid="sms-inbox-reply-input" className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-emerald-500" />
                          <div className={`text-[10px] mt-0.5 ${replyText.length > 160 ? "text-amber-400" : "text-slate-500"}`} data-testid="sms-reply-charcount">{replyText.length}/160{replyText.length > 160 ? ` · ${Math.ceil(replyText.length / 153)} SMS` : ""}</div>
                          <div className="text-[10px] text-slate-600 mt-0.5" data-testid="sms-reply-placeholders">{t("Zmienne")}: {"{nr_zgłoszenia}"} · {"{ETA}"} · {"{miasto}"}</div>
                        </div>
                        <button onClick={() => sendReply(m)} disabled={busy || !replyText.trim()} data-testid="sms-inbox-reply-send" className="text-xs bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded px-3 py-1.5 flex items-center gap-1.5 self-start">{busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} {t("Wyślij")}</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
        </div>
      </div>
    </div>
  );
}
